/**
 * Call It In LLC — Address Book
 *
 * Dispatcher / admin surface for managing saved locations:
 * vendor warehouses, hospitals, surgery centers, warehouses, customers, and
 * other frequently-used pickup or delivery sites.
 *
 * Each saved location reduces friction at task-creation time and anchors the
 * chain-of-custody record with stable contact and delivery-instruction data.
 *
 * Supports:
 *   - List with instant search and type / status filter chips
 *   - Add location (full form)
 *   - Edit location (same form pre-populated)
 *   - Archive (soft-delete) / Restore — no permanent deletion
 *   - View Details (read-only drawer)
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";

import DashboardLayout from "../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../examples/Navbars/DashboardNavbar";
import MDBox from "../components/MDBox";
import MDTypography from "../components/MDTypography";
import MDButton from "../components/MDButton";
import MDInput from "../components/MDInput";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Icon,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ComplexStatisticsCard from "../examples/Cards/StatisticsCards/ComplexStatisticsCard";
import {
  listLocations,
  createLocation,
  updateLocation,
  archiveLocation,
  restoreLocation,
} from "../api/locations";

// ── Constants ─────────────────────────────────────────────────────────────────

const LOCATION_TYPES = [
  { value: "vendor",          label: "Vendor" },
  { value: "hospital",        label: "Hospital" },
  { value: "surgery_center",  label: "Surgery Center" },
  { value: "warehouse",       label: "Warehouse" },
  { value: "customer",        label: "Customer" },
  { value: "other",           label: "Other" },
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  "DC",
];

// Node-color semantics from the AxisSync brand identity:
// hospital / surgery_center → blue (facility origin)
// vendor → orange (vendor / active coordination)
// customer → green (delivery confirmed)
// warehouse / other → gray (neutral)
const typeStyle = (type) => {
  switch (type) {
    case "hospital":
    case "surgery_center":
      return { bg: "#dbeafe", text: "#1d4ed8", border: "#93c5fd" };
    case "vendor":
      return { bg: "#ffedd5", text: "#c2410c", border: "#fdba74" };
    case "customer":
      return { bg: "#dcfce7", text: "#15803d", border: "#86efac" };
    case "warehouse":
      return { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" };
    default:
      return { bg: "#f8fafc", text: "#94a3b8", border: "#e2e8f0" };
  }
};

const TYPE_LABELS = Object.fromEntries(LOCATION_TYPES.map(({ value, label }) => [value, label]));

// ── Small shared sub-components ───────────────────────────────────────────────

function TypeBadge({ type }) {
  const s = typeStyle(type);
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 12,
      fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.04em",
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      whiteSpace: "nowrap",
    }}>
      {TYPE_LABELS[type] ?? type}
    </span>
  );
}

function ActiveBadge({ isActive }) {
  return (
    <span style={{
      display: "inline-block", padding: "2px 9px", borderRadius: 10,
      fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase",
      background: isActive ? "#dcfce7" : "#f1f5f9",
      color:      isActive ? "#16a34a" : "#94a3b8",
    }}>
      {isActive ? "Active" : "Archived"}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <Typography variant="caption" sx={{
      display: "block", fontWeight: 700, letterSpacing: "0.07em",
      textTransform: "uppercase", color: "#64748b", mt: 1, mb: 0.25,
    }}>
      {children}
    </Typography>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <MDBox display="flex" flexDirection="column" mb={1.25}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontSize: "0.63rem" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: "#1e293b", lineHeight: 1.5, whiteSpace: "pre-wrap", mt: 0.25 }}>
        {value}
      </Typography>
    </MDBox>
  );
}

function Dash() {
  return <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>—</span>;
}

// ── Confirm dialog (matches Employees.jsx pattern exactly) ────────────────────

function ConfirmDialog({ open, title, body, confirmLabel, confirmColor = "error", onConfirm, onClose, loading }) {
  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined}
      maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <MDBox sx={{ display: "flex", flexDirection: "column", gap: 1 }}>{body}</MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color={confirmColor} onClick={onConfirm}
          disabled={loading} sx={{ minWidth: 120 }}>
          {loading ? <CircularProgress size={16} color="inherit" /> : confirmLabel}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ── Location form fields (shared by Add + Edit) ───────────────────────────────

const EMPTY_FORM = {
  name: "",
  location_type: "vendor",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "CO",
  postal_code: "",
  main_phone: "",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  delivery_instructions: "",
  receiving_hours: "",
  internal_notes: "",
  is_active: true,
  _saving: false,
};

function LocationFormFields({ form, onChange }) {
  return (
    <>
      <SectionLabel>Identity</SectionLabel>
      <MDInput
        label="Location Name"
        value={form.name}
        onChange={(e) => onChange("name", e.target.value)}
        fullWidth required autoFocus={!form._editing}
        disabled={form._saving}
        placeholder="e.g. Mercy Hospital SPD, Alpha Vendor Warehouse"
      />

      <FormControl fullWidth size="small" disabled={form._saving}>
        <InputLabel id="loc-type-label">Location Type</InputLabel>
        <Select
          labelId="loc-type-label"
          value={form.location_type}
          label="Location Type"
          onChange={(e) => onChange("location_type", e.target.value)}
          sx={{ height: 44 }}
        >
          {LOCATION_TYPES.map(({ value, label }) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <SectionLabel>Address</SectionLabel>
      <MDInput
        label="Address Line 1"
        value={form.address_line_1}
        onChange={(e) => onChange("address_line_1", e.target.value)}
        fullWidth disabled={form._saving}
        placeholder="Street address, PO Box, etc."
      />
      <MDInput
        label="Address Line 2"
        value={form.address_line_2}
        onChange={(e) => onChange("address_line_2", e.target.value)}
        fullWidth disabled={form._saving}
        placeholder="Suite, dock #, building, floor"
      />

      <MDBox display="flex" gap={2}>
        <MDInput
          label="City"
          value={form.city}
          onChange={(e) => onChange("city", e.target.value)}
          fullWidth disabled={form._saving}
        />

        <FormControl sx={{ minWidth: 100 }} size="small" disabled={form._saving}>
          <InputLabel id="loc-state-label">State</InputLabel>
          <Select
            labelId="loc-state-label"
            value={form.state}
            label="State"
            onChange={(e) => onChange("state", e.target.value)}
            sx={{ height: 44 }}
          >
            {US_STATES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <MDInput
          label="ZIP"
          value={form.postal_code}
          onChange={(e) => onChange("postal_code", e.target.value)}
          sx={{ maxWidth: 110 }} disabled={form._saving}
          inputProps={{ maxLength: 10 }}
        />
      </MDBox>

      <SectionLabel>Contact</SectionLabel>
      <MDBox display="flex" gap={2}>
        <MDInput
          label="Contact Name"
          value={form.contact_name}
          onChange={(e) => onChange("contact_name", e.target.value)}
          fullWidth disabled={form._saving}
          placeholder="Receiving supervisor, SPD lead, rep name"
        />
        <MDInput
          label="Main Facility Phone"
          value={form.main_phone}
          onChange={(e) => onChange("main_phone", e.target.value)}
          fullWidth disabled={form._saving}
          placeholder="Main switchboard"
        />
      </MDBox>

      <MDBox display="flex" gap={2}>
        <MDInput
          label="Contact Direct Phone"
          value={form.contact_phone}
          onChange={(e) => onChange("contact_phone", e.target.value)}
          fullWidth disabled={form._saving}
          placeholder="Direct line or mobile"
        />
        <MDInput
          type="email"
          label="Contact Email"
          value={form.contact_email}
          onChange={(e) => onChange("contact_email", e.target.value)}
          fullWidth disabled={form._saving}
        />
      </MDBox>

      <SectionLabel>Delivery Details</SectionLabel>
      <MDInput
        label="Delivery Instructions"
        value={form.delivery_instructions}
        onChange={(e) => onChange("delivery_instructions", e.target.value)}
        fullWidth multiline rows={3} disabled={form._saving}
        placeholder="e.g. Ring bell at Decon dock — do not leave unattended. Badge required after 15:00."
      />
      <MDInput
        label="Receiving Hours"
        value={form.receiving_hours}
        onChange={(e) => onChange("receiving_hours", e.target.value)}
        fullWidth disabled={form._saving}
        placeholder="e.g. Mon–Fri 06:00–18:00 MST, Sat 08:00–12:00"
      />
      <MDInput
        label="Internal Notes"
        value={form.internal_notes}
        onChange={(e) => onChange("internal_notes", e.target.value)}
        fullWidth multiline rows={2} disabled={form._saving}
        placeholder="Parking notes, dock codes, special handling — visible to dispatch only"
      />
    </>
  );
}

// ── Add Location modal ────────────────────────────────────────────────────────

function AddLocationModal({ open, onClose, onAdded }) {
  const [form, setForm]   = useState(EMPTY_FORM);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setForm(EMPTY_FORM); setError(""); }
  }, [open]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Location name is required.");
    if (!form.location_type) return setError("Location type is required.");

    setForm((p) => ({ ...p, _saving: true }));
    try {
      const payload = buildPayload(form);
      const res  = await createLocation(payload);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onAdded();
      } else {
        setError(data?.detail ?? `Save failed (HTTP ${res.status}).`);
      }
    } catch {
      setError("Cannot reach the server. Check your connection and try again.");
    } finally {
      setForm((p) => ({ ...p, _saving: false }));
    }
  };

  return (
    <Dialog open={open} onClose={!form._saving ? onClose : undefined}
      maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Add Location to Address Book
      </DialogTitle>
      <Divider />
      <DialogContent>
        <MDBox component="form" onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
          <LocationFormFields form={form} onChange={set} />
          <MDBox display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={form._saving}>
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="success"
              disabled={form._saving} sx={{ minWidth: 160 }}>
              {form._saving
                ? <CircularProgress size={18} color="inherit" />
                : <><Icon sx={{ mr: 0.5 }}>add_location_alt</Icon>Save Location</>}
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Location modal ───────────────────────────────────────────────────────

function EditLocationModal({ location, open, onClose, onSaved }) {
  const [form, setForm]   = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (location) {
      setForm({
        ...EMPTY_FORM,
        name:                  location.name                  || "",
        location_type:         location.location_type         || "other",
        address_line_1:        location.address_line_1        || "",
        address_line_2:        location.address_line_2        || "",
        city:                  location.city                  || "",
        state:                 location.state                 || "CO",
        postal_code:           location.postal_code           || "",
        main_phone:            location.main_phone            || "",
        contact_name:          location.contact_name          || "",
        contact_phone:         location.contact_phone         || "",
        contact_email:         location.contact_email         || "",
        delivery_instructions: location.delivery_instructions || "",
        receiving_hours:       location.receiving_hours       || "",
        internal_notes:        location.internal_notes        || "",
        is_active:             location.is_active !== false,
        _saving: false,
        _editing: true,
      });
      setError("");
    }
  }, [location]);

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) return setError("Location name is required.");
    if (!form.location_type) return setError("Location type is required.");

    setForm((p) => ({ ...p, _saving: true }));
    try {
      const payload = buildPayload(form);
      const res  = await updateLocation(location.id, payload);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        onSaved({ ...location, ...payload, updated_at: data.updated_at ?? new Date().toISOString() });
      } else {
        setError(data?.detail ?? `Save failed (HTTP ${res.status}).`);
      }
    } catch {
      setError("Cannot reach the server. Check your connection and try again.");
    } finally {
      setForm((p) => ({ ...p, _saving: false }));
    }
  };

  return (
    <Dialog open={open} onClose={!form._saving ? onClose : undefined}
      maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "1rem" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        Edit Location
        {location && (
          <Typography variant="caption" sx={{ display: "block", color: "#64748b", fontWeight: 600, mt: 0.25 }}>
            {location.name}
          </Typography>
        )}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <MDBox component="form" onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
          <LocationFormFields form={form} onChange={set} />
          <MDBox display="flex" gap={1.5} justifyContent="flex-end" mt={1}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={form._saving}>
              Cancel
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info"
              disabled={form._saving} sx={{ minWidth: 140 }}>
              {form._saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

// ── View Details drawer ───────────────────────────────────────────────────────

function LocationDetailDrawer({ location, open, onClose, onEdit }) {
  if (!location) return null;

  const addr = [location.address_line_1, location.address_line_2]
    .filter(Boolean).join(", ");
  const cityStateZip = [location.city, location.state, location.postal_code]
    .filter(Boolean).join(", ");

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 0 } }}>

      {/* Header */}
      <MDBox sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #f1f5f9", position: "sticky", top: 0, bgcolor: "#fff", zIndex: 1 }}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
              {location.name}
            </MDTypography>
            <MDBox display="flex" gap={1} mt={0.75}>
              <TypeBadge type={location.location_type} />
              <ActiveBadge isActive={location.is_active} />
            </MDBox>
          </MDBox>
          <IconButton size="small" onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </MDBox>

      {/* Body */}
      <MDBox sx={{ px: 3, py: 2.5, overflowY: "auto" }}>

        {/* Address */}
        <SectionLabel>Address</SectionLabel>
        <DetailRow label="Street" value={addr || null} />
        <DetailRow label="City / State / ZIP" value={cityStateZip || null} />

        <Divider sx={{ my: 2 }} />

        {/* Contact */}
        <SectionLabel>Contact</SectionLabel>
        <DetailRow label="Contact Name"  value={location.contact_name} />
        <DetailRow label="Direct Phone"  value={location.contact_phone} />
        <DetailRow label="Email"         value={location.contact_email} />
        <DetailRow label="Main Facility Phone" value={location.main_phone} />

        <Divider sx={{ my: 2 }} />

        {/* Delivery details */}
        <SectionLabel>Delivery Details</SectionLabel>
        <DetailRow label="Receiving Hours"       value={location.receiving_hours} />
        <DetailRow label="Delivery Instructions" value={location.delivery_instructions} />

        {location.internal_notes && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionLabel>Internal Notes</SectionLabel>
            <MDBox sx={{ bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: "0.5rem", px: 2, py: 1.5, mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: "#92400e", whiteSpace: "pre-wrap", fontSize: "0.8rem" }}>
                {location.internal_notes}
              </Typography>
            </MDBox>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Meta */}
        <SectionLabel>Record Info</SectionLabel>
        <DetailRow label="Record ID"    value={location.id} />
        <DetailRow label="Last Updated" value={location.updated_at ? new Date(location.updated_at).toLocaleString() : null} />
        <DetailRow label="Created"      value={location.created_at ? new Date(location.created_at).toLocaleString() : null} />
      </MDBox>

      {/* Footer */}
      <MDBox sx={{ px: 3, py: 2, borderTop: "1px solid #f1f5f9", display: "flex", gap: 1.5, position: "sticky", bottom: 0, bgcolor: "#fff" }}>
        <MDButton variant="gradient" color="info" fullWidth
          onClick={() => { onClose(); onEdit(location); }}
          startIcon={<Icon>edit</Icon>}>
          Edit Location
        </MDButton>
        <MDButton variant="outlined" color="secondary" onClick={onClose} sx={{ minWidth: 80 }}>
          Close
        </MDButton>
      </MDBox>
    </Drawer>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildPayload(form) {
  return {
    name:                  form.name.trim(),
    location_type:         form.location_type,
    address_line_1:        form.address_line_1.trim(),
    address_line_2:        form.address_line_2.trim()        || null,
    city:                  form.city.trim()                  || null,
    state:                 form.state                        || null,
    postal_code:           form.postal_code.trim()           || null,
    main_phone:            form.main_phone.trim()            || null,
    contact_name:          form.contact_name.trim()          || null,
    contact_phone:         form.contact_phone.trim()         || null,
    contact_email:         form.contact_email.trim()         || null,
    delivery_instructions: form.delivery_instructions.trim() || null,
    receiving_hours:       form.receiving_hours.trim()       || null,
    internal_notes:        form.internal_notes.trim()        || null,
  };
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
}

// ── Main page ─────────────────────────────────────────────────────────────────

const TYPE_FILTER_OPTIONS = [
  { key: "all",           label: "All Types" },
  { key: "vendor",        label: "Vendors" },
  { key: "hospital",      label: "Hospitals" },
  { key: "surgery_center",label: "Surgery Centers" },
  { key: "warehouse",     label: "Warehouses" },
  { key: "customer",      label: "Customers" },
  { key: "other",         label: "Other" },
];

const STATUS_FILTER_OPTIONS = [
  { key: "active",   label: "Active" },
  { key: "archived", label: "Archived" },
  { key: "all",      label: "All" },
];

export default function AddressBook() {
  const [allLocations, setAll]    = useState([]);
  const [loadingList, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [snackbar, setSnackbar]   = useState({ open: false, message: "", severity: "success" });

  // Filters (all client-side — one fetch loads all active records)
  const [typeFilter,   setType]   = useState("all");
  const [statusFilter, setStatus] = useState("active");
  const [search,       setSearch] = useState("");

  // Modal state
  const [addOpen,      setAddOpen]      = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [confirmDialog, setConfirm]     = useState({
    open: false, type: null, location: null, loading: false, error: "",
  });

  // ── Data fetch ────────────────────────────────────────────────────────────

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      // Fetch all (active + archived) so client-side status filter is instant.
      // The ?active= param is omitted to return both — the backend respects omission.
      const res  = await listLocations({ limit: 500 });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.items) {
        setAll(data.items);
      } else {
        setListError(data?.detail ?? `Failed to load Address Book (HTTP ${res.status}).`);
      }
    } catch {
      setListError("Cannot reach the server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  // ── Stats ─────────────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const active = allLocations.filter((l) => l.is_active);
    return {
      total:    active.length,
      vendors:  active.filter((l) => l.location_type === "vendor").length,
      hospitals: active.filter((l) =>
        l.location_type === "hospital" || l.location_type === "surgery_center"
      ).length,
      warehouses: active.filter((l) => l.location_type === "warehouse").length,
    };
  }, [allLocations]);

  // ── Client-side filter ────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = [...allLocations];

    // Status
    if (statusFilter === "active")   list = list.filter((l) => l.is_active);
    if (statusFilter === "archived") list = list.filter((l) => !l.is_active);

    // Type
    if (typeFilter !== "all") list = list.filter((l) => l.location_type === typeFilter);

    // Search (matches Employees pattern)
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((l) =>
        (l.name            || "").toLowerCase().includes(q) ||
        (l.address_line_1  || "").toLowerCase().includes(q) ||
        (l.city            || "").toLowerCase().includes(q) ||
        (l.contact_name    || "").toLowerCase().includes(q) ||
        (l.main_phone      || "").includes(q) ||
        (l.contact_phone   || "").includes(q)
      );
    }

    // Sort: active first, then alphabetical
    list.sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });

    return list;
  }, [allLocations, statusFilter, typeFilter, search]);

  // ── Snackbar helper ───────────────────────────────────────────────────────

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // ── Archive / Restore handlers ────────────────────────────────────────────

  const openArchive = (loc) =>
    setConfirm({ open: true, type: "archive", location: loc, loading: false, error: "" });

  const openRestore = (loc) =>
    setConfirm({ open: true, type: "restore", location: loc, loading: false, error: "" });

  const handleConfirmAction = async () => {
    const { type, location } = confirmDialog;
    setConfirm((p) => ({ ...p, loading: true, error: "" }));
    try {
      const res  = type === "archive"
        ? await archiveLocation(location.id)
        : await restoreLocation(location.id);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setConfirm((p) => ({ ...p, open: false, loading: false }));
        setAll((prev) =>
          prev.map((l) =>
            l.id === location.id ? { ...l, is_active: type !== "archive" } : l
          )
        );
        notify(
          type === "archive"
            ? `"${location.name}" archived.`
            : `"${location.name}" restored to active.`
        );
      } else {
        setConfirm((p) => ({
          ...p, loading: false,
          error: data?.detail ?? `Action failed (HTTP ${res.status}).`,
        }));
      }
    } catch {
      setConfirm((p) => ({ ...p, loading: false, error: "Cannot reach the server." }));
    }
  };

  // ── Add / Edit callbacks ──────────────────────────────────────────────────

  const handleAdded = () => {
    setAddOpen(false);
    notify("Location saved to Address Book.");
    fetchLocations();
  };

  const handleEditSaved = (updated) => {
    setEditTarget(null);
    setAll((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    notify(`"${updated.name}" updated.`);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* Stats */}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <ComplexStatisticsCard
                color="info" icon="location_on"
                title="Active Locations" count={stats.total}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <ComplexStatisticsCard
                color="warning" icon="business"
                title="Vendors" count={stats.vendors}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <ComplexStatisticsCard
                color="primary" icon="local_hospital"
                title="Hospitals & Surgery Centers" count={stats.hospitals}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <ComplexStatisticsCard
                color="secondary" icon="warehouse"
                title="Warehouses" count={stats.warehouses}
              />
            </Grid>
          </Grid>
        </MDBox>

        <Card sx={{ borderRadius: "1.25rem" }}>

          {/* Header */}
          <MDBox pt={3} px={3} pb={0} display="flex" justifyContent="space-between"
            alignItems="center" flexWrap="wrap" gap={1}>
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Address Book
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: "#475569" }}>
                Saved vendors, hospitals, surgery centers, and delivery sites
              </MDTypography>
            </MDBox>
            <MDButton
              variant="gradient" color="success" size="small"
              onClick={() => setAddOpen(true)}
              startIcon={<Icon>add_location_alt</Icon>}
            >
              Add Location
            </MDButton>
          </MDBox>

          {/* Filters */}
          <MDBox px={3} pt={2.5} pb={1.5} display="flex" flexWrap="wrap" gap={1.5} alignItems="center">

            {/* Type filter */}
            <MDBox display="flex" flexWrap="wrap" gap={0.6}>
              {TYPE_FILTER_OPTIONS.map(({ key, label }) => (
                <Chip
                  key={key} size="small" label={label}
                  variant={typeFilter === key ? "filled" : "outlined"}
                  color={typeFilter === key ? "info" : "default"}
                  onClick={() => setType(key)}
                  sx={{ cursor: "pointer", fontWeight: 600 }}
                />
              ))}
            </MDBox>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />

            {/* Status filter */}
            <MDBox display="flex" gap={0.6}>
              {STATUS_FILTER_OPTIONS.map(({ key, label }) => (
                <Chip
                  key={key} size="small" label={label}
                  variant={statusFilter === key ? "filled" : "outlined"}
                  color={
                    statusFilter === key
                      ? key === "active" ? "success" : key === "archived" ? "default" : "primary"
                      : "default"
                  }
                  onClick={() => setStatus(key)}
                  sx={{ cursor: "pointer", fontWeight: 600 }}
                />
              ))}
            </MDBox>

            {/* Search */}
            <MDBox ml="auto">
              <MDInput
                placeholder="Search name, address, city, contact, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ fontSize: "1rem !important", color: "text.secondary" }}>search</Icon>
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>
                        <Icon sx={{ fontSize: "0.9rem !important" }}>close</Icon>
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </MDBox>
          </MDBox>

          {listError && (
            <MDBox px={3} pb={1}>
              <Alert severity="warning" onClose={() => setListError("")}>{listError}</Alert>
            </MDBox>
          )}

          {/* Table */}
          <MDBox sx={{ overflowX: "auto" }}>
            {loadingList ? (
              <MDBox display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} />
              </MDBox>
            ) : filtered.length === 0 ? (
              <MDBox py={5} textAlign="center">
                <MDTypography variant="body2" sx={{ color: "#475569" }}>
                  {search || typeFilter !== "all" || statusFilter !== "active"
                    ? "No locations match the current filters."
                    : "No locations saved yet. Add your first location above."}
                </MDTypography>
              </MDBox>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                    {["Name", "Type", "City / State", "Main Contact", "Phone", "Status", "Updated", "Actions"].map((h) => (
                      <th key={h} style={{
                        padding: "10px 16px", textAlign: "left",
                        fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.07em",
                        textTransform: "uppercase", color: "#334155", whiteSpace: "nowrap",
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((loc) => {
                    const isActive = loc.is_active;
                    const cityState = [loc.city, loc.state].filter(Boolean).join(", ");

                    return (
                      <tr key={loc.id} style={{
                        borderBottom: "1px solid #f1f5f9",
                        background: isActive ? "transparent" : "#fafafa",
                        opacity: isActive ? 1 : 0.72,
                        transition: "background 0.1s",
                      }}>

                        {/* Name */}
                        <td style={{ padding: "13px 16px", minWidth: 180 }}>
                          <button
                            onClick={() => setDetailTarget(loc)}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              textAlign: "left", padding: 0,
                            }}
                          >
                            <div style={{ fontWeight: 600, fontSize: "0.85rem", color: "#1e293b" }}>
                              {loc.name}
                            </div>
                            {loc.address_line_1 && (
                              <div style={{ fontSize: "0.71rem", color: "#64748b", marginTop: 2 }}>
                                {loc.address_line_1}
                              </div>
                            )}
                          </button>
                        </td>

                        {/* Type */}
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                          <TypeBadge type={loc.location_type} />
                        </td>

                        {/* City / State */}
                        <td style={{ padding: "13px 16px", minWidth: 120 }}>
                          {cityState
                            ? <span style={{ fontSize: "0.78rem", color: "#475569" }}>{cityState}</span>
                            : <Dash />}
                        </td>

                        {/* Contact */}
                        <td style={{ padding: "13px 16px", minWidth: 140 }}>
                          {loc.contact_name
                            ? <span style={{ fontSize: "0.78rem", color: "#475569" }}>{loc.contact_name}</span>
                            : <Dash />}
                        </td>

                        {/* Phone */}
                        <td style={{ padding: "13px 16px", minWidth: 120 }}>
                          {loc.main_phone || loc.contact_phone
                            ? <span style={{ fontSize: "0.78rem", color: "#475569" }}>
                                {loc.main_phone || loc.contact_phone}
                              </span>
                            : <Dash />}
                        </td>

                        {/* Status */}
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                          <ActiveBadge isActive={isActive} />
                        </td>

                        {/* Updated */}
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap", minWidth: 100 }}>
                          <span style={{ fontSize: "0.73rem", color: "#64748b" }}>
                            {formatDate(loc.updated_at)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td style={{ padding: "13px 16px", whiteSpace: "nowrap" }}>
                          <MDBox display="flex" gap={0.75} alignItems="center">
                            <Tooltip title="View full details" arrow>
                              <MDButton
                                variant="outlined" color="secondary" size="small"
                                onClick={() => setDetailTarget(loc)}
                                sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: "0.72rem" }}
                              >
                                <Icon sx={{ fontSize: "0.95rem !important" }}>visibility</Icon>
                              </MDButton>
                            </Tooltip>

                            <Tooltip title="Edit location" arrow>
                              <MDButton
                                variant="outlined" color="info" size="small"
                                onClick={() => setEditTarget(loc)}
                                sx={{ minWidth: 0, px: 1.1, py: 0.5, fontSize: "0.72rem" }}
                              >
                                <Icon sx={{ fontSize: "0.95rem !important", mr: 0.4 }}>edit</Icon>
                                Edit
                              </MDButton>
                            </Tooltip>

                            {isActive ? (
                              <Tooltip title="Archive this location" arrow>
                                <MDButton
                                  variant="outlined" color="warning" size="small"
                                  onClick={() => openArchive(loc)}
                                  sx={{ minWidth: 0, px: 1.1, py: 0.5, fontSize: "0.72rem" }}
                                >
                                  <Icon sx={{ fontSize: "0.95rem !important", mr: 0.4 }}>archive</Icon>
                                  Archive
                                </MDButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Restore to active" arrow>
                                <MDButton
                                  variant="outlined" color="success" size="small"
                                  onClick={() => openRestore(loc)}
                                  sx={{ minWidth: 0, px: 1.1, py: 0.5, fontSize: "0.72rem" }}
                                >
                                  <Icon sx={{ fontSize: "0.95rem !important", mr: 0.4 }}>unarchive</Icon>
                                  Restore
                                </MDButton>
                              </Tooltip>
                            )}
                          </MDBox>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </MDBox>

          {/* Footer count */}
          {!loadingList && filtered.length > 0 && (
            <MDBox px={3} py={1.5} borderTop="1px solid #f1f5f9">
              <MDTypography variant="caption" sx={{ color: "#475569" }}>
                Showing {filtered.length} of {allLocations.length} locations
                {typeFilter !== "all" && ` · ${TYPE_LABELS[typeFilter] ?? typeFilter}`}
                {statusFilter !== "all" && ` · ${statusFilter === "active" ? "Active" : "Archived"}`}
              </MDTypography>
            </MDBox>
          )}
        </Card>
      </MDBox>

      {/* ── Inline snackbar ────────────────────────────────────────────────── */}
      {snackbar.open && (
        <MDBox
          sx={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            zIndex: 9999, minWidth: 280,
          }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
            sx={{ boxShadow: 3, borderRadius: "0.75rem" }}
          >
            {snackbar.message}
          </Alert>
        </MDBox>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      <AddLocationModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={handleAdded}
      />

      <EditLocationModal
        location={editTarget}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={handleEditSaved}
      />

      <LocationDetailDrawer
        location={detailTarget}
        open={!!detailTarget}
        onClose={() => setDetailTarget(null)}
        onEdit={(loc) => { setDetailTarget(null); setEditTarget(loc); }}
      />

      <ConfirmDialog
        open={confirmDialog.open}
        title={
          confirmDialog.type === "archive"
            ? `Archive "${confirmDialog.location?.name}"?`
            : `Restore "${confirmDialog.location?.name}"?`
        }
        body={
          <>
            {confirmDialog.error && (
              <Alert severity="error" sx={{ fontSize: "0.78rem" }}>{confirmDialog.error}</Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              {confirmDialog.type === "archive"
                ? "This location will be hidden from the default list and will no longer appear as a suggestion at task creation. Existing tasks that referenced it retain their address snapshots. This action is reversible."
                : "This will restore the location to active status and make it available for task creation again."}
            </Typography>
          </>
        }
        confirmLabel={confirmDialog.type === "archive" ? "Archive" : "Restore"}
        confirmColor={confirmDialog.type === "archive" ? "warning" : "success"}
        onConfirm={handleConfirmAction}
        onClose={() => setConfirm((p) => ({ ...p, open: false }))}
        loading={confirmDialog.loading}
      />
    </DashboardLayout>
  );
}
